import React, { useState, useEffect } from 'react';
import {
  CheckSquare,
  Clock,
  Calendar,
  TrendingUp,
  CheckCircle,
  Target,
  BarChart3,
  Timer
} from 'lucide-react';
import { useAuthStore } from '../auth/authStore';
import { UserDashboardLayout } from './UserDashboardLayout';
import { AuthDebug } from '../../components/AuthDebug';
import ErrorBoundary from '../../components/ErrorBoundary';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface UserDashboardOverview {
  tasks: {
    assigned: number;
    completed: number;
    overdue: number;
    inProgress: number;
  };
  timeTracking: {
    todayHours: number;
    weekHours: number;
    monthHours: number;
    activeTimer: boolean;
  };
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: string;
  }>;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, change, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <p className={`text-sm ${
            trend === 'up' ? 'text-green-600' : 
            trend === 'down' ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {change}
          </p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const UserDashboardSimple: React.FC = () => {
  const { user, isAuthenticated, accessToken } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<UserDashboardOverview | null>(null);

  const isManager = user?.roles?.includes('manager') || user?.roles?.includes('admin');

  const handleQuickLogin = async () => {
    try {
      const { login } = useAuthStore.getState();
      await login('admin@demo.com', 'Demo123!', 'demo');
      toast.success('Logged in successfully!');
    } catch (error) {
      console.error('Quick login failed:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!isAuthenticated || !user || !accessToken) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Simulate API call with mock data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const mockOverview: UserDashboardOverview = {
          tasks: {
            assigned: 12,
            completed: 8,
            overdue: 2,
            inProgress: 4,
          },
          timeTracking: {
            todayHours: 6.5,
            weekHours: 32.5,
            monthHours: 142.0,
            activeTimer: false,
          },
          recentActivity: [
            { type: 'task', description: 'Completed task: Review project proposal', timestamp: new Date().toISOString() },
            { type: 'task', description: 'Started task: Update documentation', timestamp: new Date(Date.now() - 3600000).toISOString() },
            { type: 'timesheet', description: 'Submitted timesheet for approval', timestamp: new Date(Date.now() - 7200000).toISOString() },
          ],
        };

        setOverview(mockOverview);
        toast('Dashboard loaded successfully', { icon: '✅' });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard data. Please try again.');
        setOverview(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, user, accessToken]);

  const handleNavigateToTasks = () => {
    navigate('/user-tasks');
  };

  const handleNavigateToTimesheet = () => {
    navigate('/user-timesheet');
  };

  // Show login prompt if not authenticated
  if (!isAuthenticated || !user || !accessToken) {
    return (
      <UserDashboardLayout>
        <div className="space-y-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
            <p className="text-gray-600 mb-6">Please log in to access your dashboard</p>
            <button
              onClick={handleQuickLogin}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Quick Login (Demo)
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Using demo credentials: admin@demo.com / Demo123! / firm: demo
            </p>
          </div>
          <AuthDebug />
        </div>
      </UserDashboardLayout>
    );
  }

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading dashboard...</span>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <ErrorBoundary>
        <div className="p-6">
          {/* Welcome Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome back, {user?.firstName}!
            </h2>
            <p className="text-gray-600">
              {isManager 
                ? "Here's an overview of your tasks and team performance."
                : "Here's an overview of your tasks and productivity."
              }
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Assigned Tasks"
              value={overview?.tasks.assigned || 0}
              change="+2 this week"
              icon={CheckSquare}
              color="bg-blue-500"
              trend="up"
            />
            <StatCard
              title="Completed Tasks"
              value={overview?.tasks.completed || 0}
              change="+5 this week"
              icon={CheckCircle}
              color="bg-green-500"
              trend="up"
            />
            <StatCard
              title="Hours Today"
              value={`${overview?.timeTracking.todayHours || 0}h`}
              change="Target: 8h"
              icon={Clock}
              color="bg-purple-500"
              trend="neutral"
            />
            <StatCard
              title="This Month"
              value={`${overview?.timeTracking.monthHours || 0}h`}
              change="+12h vs last month"
              icon={BarChart3}
              color="bg-orange-500"
              trend="up"
            />
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={handleNavigateToTasks}
                  className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center">
                    <Target className="w-5 h-5 text-blue-600 mr-3" />
                    <span className="text-blue-900 font-medium">View My Tasks</span>
                  </div>
                  <span className="text-blue-600 text-sm">{overview?.tasks.assigned || 0} pending</span>
                </button>
                
                <button
                  onClick={handleNavigateToTimesheet}
                  className="w-full flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center">
                    <Timer className="w-5 h-5 text-green-600 mr-3" />
                    <span className="text-green-900 font-medium">Time Tracking</span>
                  </div>
                  <span className="text-green-600 text-sm">{overview?.timeTracking.todayHours || 0}h today</span>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {overview?.recentActivity.slice(0, 3).map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.description}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(activity.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <AuthDebug />
      </ErrorBoundary>
    </UserDashboardLayout>
  );
};

export default UserDashboardSimple;
